<template>
  <section class="fund-earn">
    <h3 class="fund-earn__title">How You Earn</h3>
    <div v-if="rate > 0" class="fund-earn__grid">
      <div>
        <span class="fund-earn__label">{{ holding > 0 ? "Your Holding Today" : `${money(base)} Today` }}</span>
        <span class="fund-earn__value">≈ {{ money(base * (1 + rate / 100)) }}</span>
        <span class="fund-earn__sub">in a year at today’s rate, not guaranteed</span>
      </div>
      <div>
        <span class="fund-earn__label">Earned Per Day</span>
        <span class="fund-earn__value">≈ {{ money(base * (rate / 100) / 365) }}</span>
        <span class="fund-earn__sub">on {{ holding > 0 ? "your holding" : `every ${money(base)}` }}</span>
      </div>
      <div>
        <span class="fund-earn__label">Selling</span>
        <span class="fund-earn__value fund-earn__value--text">Any day</span>
        <span class="fund-earn__sub">at that day’s unit price</span>
      </div>
    </div>
    <p v-else class="fund-earn__note">
      The unit price is the fund’s net asset value, set by the manager. You can sell any day at the
      current price.
    </p>
    <dl v-if="rate > 0" class="fund-earn__rates">
      <div>
        <dt>{{ rateSince ? `From ${date(rateSince)}` : "Since launch" }}</dt>
        <dd>{{ rate }}% a year</dd>
      </div>
    </dl>
    <p v-if="rate > 0" class="fund-earn__note">
      No coupons to wait for: the rate is added to the unit price every day. It follows the market
      and can change.
    </p>
  </section>
</template>

<script>
import { money } from "@/utils/exchange.js";

/**
 * How an accruing fund pays: nothing out, the rate added to the unit price daily. What a holding
 * (or a typical first investment) grows to in a year and earns per day at today's rate, and since
 * when the current rate has applied (the price checkpoint the backend sets when it changes).
 */
export default {
  name: "FundEarn",
  props: {
    token: { type: Object, required: true },
    holding: { type: Number, default: 0 },
    price: { type: Number, default: 0 },
  },
  computed: {
    currency() { return this.token.currency || "NGN"; },
    rate() { return Number(this.token.interest) || 0; },
    base() {
      if (this.holding > 0) return this.holding * this.price;
      return { NGN: 100000, KES: 10000 }[this.currency] || 1000;
    },
    rateSince() {
      const at = this.token.fund_pricing?.checkpoint_at;
      const launched = this.token.createdAt;
      // A checkpoint on launch day is the launch, not a change of rate.
      if (!at || (launched && new Date(at) - new Date(launched) < 86400000)) return null;
      return at;
    },
  },
  methods: {
    money(value) { return money(value, this.currency, 2); },
    date(value) { return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #94a3b8;
$border-color: #e2e8f0;
$success: #10b981;

.fund-earn {
  background: #ffffff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 20px;

  &__title { font-size: 15px; font-weight: 600; color: $text-primary; margin: 0 0 12px; }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    @media (max-width: 767px) { grid-template-columns: 1fr; }
    > div { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  }

  &__label { font-size: 12px; color: $text-secondary; }
  &__value { font-size: 18px; font-weight: 600; color: $success; &--text { color: $text-primary; } }
  &__sub { font-size: 12px; color: $text-muted; }

  &__rates {
    margin: 14px 0 0;
    border-top: 1px solid $border-color;
    padding-top: 12px;
    > div { display: flex; justify-content: space-between; font-size: 13px; line-height: 1.8; }
    dt { color: $text-secondary; }
    dd { margin: 0; color: $text-primary; font-weight: 600; }
  }

  &__note { font-size: 12px; color: $text-muted; margin: 10px 0 0; line-height: 1.5; }
}
</style>
